/**
 * AttentionQueue - Actionable items list for the Smart Queue Dashboard
 *
 * Shows a prioritized list of items requiring attention:
 * - Upcoming earnings
 * - Thesis allocation drift
 * - Material negative news
 * - Recently triggered alerts
 */
import { Link } from 'react-router-dom'
import { Badge, Card, CardHeader, CardBody } from '../common'
import type { AttentionItem, AttentionCategory } from '../../hooks/useAttentionQueue'

interface AttentionQueueProps {
  items: AttentionItem[]
  counts: {
    earnings: number
    drift: number
    news: number
    alerts: number
    total: number
  }
  isLoading?: boolean
  error?: Error | null
}

// Category config
const CATEGORY_CONFIG: Record<
  AttentionCategory,
  { label: string; intent: 'info' | 'warning' | 'danger' | 'neutral'; icon: string }
> = {
  earnings: { label: 'EARNINGS', intent: 'info', icon: '📅' },
  drift: { label: 'DRIFT', intent: 'warning', icon: '⚖️' },
  news: { label: 'NEWS', intent: 'danger', icon: '📰' },
  alert: { label: 'ALERT', intent: 'danger', icon: '🔔' },
}

function AttentionItemRow({ item }: { item: AttentionItem }) {
  const config = CATEGORY_CONFIG[item.category]

  const content = (
    <div className="flex items-center gap-3 py-3 px-4 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 group">
      {/* Category badge */}
      <Badge intent={config.intent} variant="soft" size="sm">
        {config.label}
      </Badge>

      {/* Priority indicator (for urgent/high only) */}
      {(item.priority === 'urgent' || item.priority === 'high') && (
        <span
          className={`w-2 h-2 rounded-full ${
            item.priority === 'urgent' ? 'bg-red-500' : 'bg-orange-500'
          }`}
          title={`${item.priority} priority`}
        />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
        {item.subtitle && (
          <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>
        )}
      </div>

      {/* Symbol (if present) */}
      {item.symbol && (
        <span className="text-xs font-mono text-gray-400 hidden sm:block">
          {item.symbol}
        </span>
      )}

      {/* Arrow */}
      <svg
        className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  )

  // Wrap in link if action URL is present
  if (item.actionUrl) {
    return (
      <Link to={item.actionUrl} className="block">
        {content}
      </Link>
    )
  }

  return content
}

function EmptyQueue() {
  return (
    <div className="py-12 text-center">
      <div className="text-4xl mb-2">✨</div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">All caught up!</h3>
      <p className="text-sm text-gray-500">No items requiring attention right now.</p>
    </div>
  )
}

function LoadingQueue() {
  return (
    <div className="space-y-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 py-3 px-4 animate-pulse">
          <div className="w-16 h-5 bg-gray-200 rounded" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
            <div className="h-3 bg-gray-100 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function AttentionQueue({ items, counts, isLoading, error }: AttentionQueueProps) {
  return (
    <Card padding="none">
      <CardHeader
        title="Attention Queue"
        action={
          counts.total > 0 ? (
            <div className="flex gap-2">
              {counts.earnings > 0 && (
                <Badge intent="info" variant="soft" size="sm">
                  {counts.earnings} earnings
                </Badge>
              )}
              {counts.drift > 0 && (
                <Badge intent="warning" variant="soft" size="sm">
                  {counts.drift} drift
                </Badge>
              )}
              {counts.news > 0 && (
                <Badge intent="danger" variant="soft" size="sm">
                  {counts.news} news
                </Badge>
              )}
              {counts.alerts > 0 && (
                <Badge intent="danger" variant="soft" size="sm">
                  {counts.alerts} alerts
                </Badge>
              )}
            </div>
          ) : null
        }
      />
      <CardBody className="!p-0">
        {error ? (
          <div className="py-8 text-center text-red-600">
            <p>Failed to load attention items</p>
            <p className="text-sm text-red-500">{error.message}</p>
          </div>
        ) : isLoading ? (
          <LoadingQueue />
        ) : items.length === 0 ? (
          <EmptyQueue />
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((item) => (
              <AttentionItemRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}
